import Parser from 'html-react-parser';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import ExternalLink from '@/common/components/atoms/ExternalLink/ExternalLink';
import Picture from '@/common/components/atoms/Picture/Picture';
import Video from '@/common/components/atoms/Video/Video';
import { ROUTE_LEGALS, ROUTE_LOGIN } from '@/common/constants';
import { useConfig } from '@/common/contexts/ConfigContext';
import formatSrcSet from '@/common/helpers/formatSrcSet';
import getImageBySize from '@/common/helpers/getImageBySize';
import usePromotionBlock from '@/common/hooks/promotion-block/usePromotionBlock';
import useTheme from '@/common/hooks/useTheme';
import SessionStorageAuth from '@/common/services/SessionStorage.auth';
import { LOGIN_PROMOTION_BLOCK_FORMATS } from '@/ebat/constants';

import {
  LoginTemplateDefaultProps,
  LoginTemplatePropTypes,
} from './LoginTemplate.proptypes';

const LoginTemplate = ({ children, className }) => {
  const { t } = useTranslation();
  const { Logo } = useTheme();
  const { env } = useConfig();

  const authService = SessionStorageAuth.getService();

  const { data: promotionBlock, isSuccess } = usePromotionBlock({
    type: 'block',
    limit: 3,
  });

  const onMessage = (e) => {
    const { token, refresh_token: refreshToken, user } = e.data;

    if (token && refreshToken && user) {
      authService.setToken(token, refreshToken);

      window.location.reload();
    }
  };

  useEffect(() => {
    window.addEventListener('message', onMessage, true);

    return () => {
      window.removeEventListener('message', onMessage, true);
    };
  });

  return (
    <div className={`page-login${className ? ` ${className}` : ''}`}>
      {/*<iframe*/}
      {/*  title={t(`${partnerEnv}:entities:common.general.title`)}*/}
      {/*  src={`${partnerHost}/login`}*/}
      {/*  style={{ display: 'none' }}*/}
      {/*/>*/}
      <div className="page-login__wrapper">
        <div className="page-login__description">
          <NavLink to={ROUTE_LOGIN} className="site-link">
            <Logo className="site-logo" />
          </NavLink>
          <h1 className="h1" data-qa-id="title-h1">
            {t('ebat:entities:login.description.title', {
              env: t(`${env}:messages:title`),
            })}
          </h1>
          <p className="subtitle">
            {t('ebat:entities:login.description.subtitle')}
          </p>
          <div className="description">
            {Parser(t('ebat:entities:login.description.details'))}
          </div>
        </div>
        <div className="page-login__links">
          <ul className="list-grid">
            {isSuccess &&
              promotionBlock &&
              promotionBlock.data.map((promotion) => (
                <li
                  key={`promotion_${promotion.id}`}
                  className={`list-grid__item ${promotion.position
                    .split(' ')
                    .join('-')}`}
                >
                  {promotion.link_video ? (
                    <Video
                      className="list-grid__video"
                      src={promotion.link_video}
                      title={promotion.title}
                    />
                  ) : (
                    <ExternalLink
                      className="list-grid__link"
                      href={promotion.link}
                    >
                      {promotion?.images?.length > 0 && (
                        <Picture
                          alt={promotion.images[0].alt}
                          src={getImageBySize(
                            promotion.images[0].url,
                            LOGIN_PROMOTION_BLOCK_FORMATS[
                              promotion.images[0].destination
                            ],
                          )}
                          srcSet={formatSrcSet(
                            promotion.images,
                            LOGIN_PROMOTION_BLOCK_FORMATS,
                          )}
                          type="static"
                        />
                      )}
                    </ExternalLink>
                  )}
                </li>
              ))}
          </ul>
        </div>
        <NavLink className="legal-link" to={ROUTE_LEGALS}>
          {t('ebat:entities:layout.footer.cgv')}
        </NavLink>
        <div className="page-login__connect">
          <div className="block-border">{children}</div>
        </div>
      </div>
    </div>
  );
};

LoginTemplate.defaultProps = {
  ...LoginTemplateDefaultProps,
};

LoginTemplate.propTypes = {
  ...LoginTemplatePropTypes,
};

export default LoginTemplate;
